import {
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import BusinessIcon from '@mui/icons-material/Business';
import LayersIcon from '@mui/icons-material/Layers';
import PaymentIcon from '@mui/icons-material/Payment';
import HistoryIcon from '@mui/icons-material/History';
import WorkIcon from '@mui/icons-material/Work';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import SearchIcon from '@mui/icons-material/Search';
import { useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AccessDeniedAlert } from '../auth';
import { TweaksPanel } from '../ui';

const SIDEBAR_WIDTH = 244;

// ─── Navigation structure ────────────────────────────────────────────────────

type NavItem = {
  label: string;
  path: string;
  icon: ReactNode;
};

type NavGroup = {
  title: string;
  items: NavItem[];
};

const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Platform',
    items: [
      { label: 'Dashboard', path: '/admin', icon: <DashboardIcon sx={{ fontSize: 16 }} /> },
      { label: 'Companies', path: '/admin/companies', icon: <BusinessIcon sx={{ fontSize: 16 }} /> },
      { label: 'Plans', path: '/admin/plans', icon: <LayersIcon sx={{ fontSize: 16 }} /> },
    ],
  },
  {
    title: 'Operations',
    items: [
      { label: 'API Keys', path: '/admin/api-keys', icon: <VpnKeyIcon sx={{ fontSize: 16 }} /> },
      { label: 'Billing', path: '/admin/billing', icon: <PaymentIcon sx={{ fontSize: 16 }} /> },
      { label: 'Audit Log', path: '/admin/audit', icon: <HistoryIcon sx={{ fontSize: 16 }} /> },
      { label: 'Job Queue', path: '/admin/jobs', icon: <WorkIcon sx={{ fontSize: 16 }} /> },
    ],
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

export default function AdminLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const currentPath = location.pathname;
  const userInitial = user?.firstName?.charAt(0) || user?.email?.charAt(0)?.toUpperCase() || 'A';
  const userName = user?.firstName
    ? `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`
    : user?.email || 'Admin';

  // ─── Sidebar content ────────────────────────────────────────────────────

  const sidebarContent = (
    <aside
      style={{
        width: SIDEBAR_WIDTH,
        height: '100%',
        flexShrink: 0,
        borderRight: '1px solid var(--line)',
        background: 'linear-gradient(180deg, var(--bg-0), var(--canvas-edge))',
        display: 'flex',
        flexDirection: 'column',
        padding: '14px 12px',
      }}
    >
      {/* Workspace / Admin section */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 10px',
          marginBottom: 14,
          borderRadius: 10,
          border: '1px solid var(--line)',
          background: 'var(--bg-1)',
        }}
      >
        <span
          style={{
            width: 26,
            height: 26,
            borderRadius: 7,
            background: 'linear-gradient(135deg, var(--crit), oklch(from var(--crit) calc(l - .1) c calc(h + 10)))',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--bg-0)',
          }}
        >
          A
        </span>
        <span style={{ flex: 1, display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>Admin Panel</span>
          <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>Platform management</span>
        </span>
      </div>

      {/* Search / Command palette trigger */}
      <button
        type="button"
        style={{
          all: 'unset',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 10px',
          marginBottom: 16,
          borderRadius: 8,
          border: '1px solid var(--line)',
          background: 'var(--bg-2)',
          color: 'var(--fg-3)',
          fontSize: 12.5,
        }}
      >
        <SearchIcon sx={{ fontSize: 14 }} />
        <span style={{ flex: 1 }}>Search or ask…</span>
        <span className="kbd">⌘</span>
        <span className="kbd">K</span>
      </button>

      {/* Grouped navigation */}
      {NAV_GROUPS.map((group) => (
        <NavGroupSection
          key={group.title}
          title={group.title}
          items={group.items}
          currentPath={currentPath}
          onNavigate={isMobile ? handleDrawerToggle : undefined}
        />
      ))}

      <div style={{ flex: 1 }} />

      {/* User section */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px' }}>
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: 99,
            background: 'linear-gradient(135deg, oklch(62% 0.12 22), oklch(48% 0.12 10))',
            fontSize: 12,
            fontWeight: 600,
            color: 'oklch(98% 0.02 22)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {userInitial}
        </div>
        <div style={{ flex: 1, lineHeight: 1.2, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName}</div>
          <div style={{ fontSize: 11, color: 'var(--fg-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</div>
        </div>
        <IconButton
          size="small"
          onClick={handleLogout}
          aria-label="Logout"
          sx={{ color: 'var(--fg-3)', '&:hover': { color: 'var(--crit)' } }}
        >
          <LogoutIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </div>
    </aside>
  );

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Sidebar */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, border: 'none' } }}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        sidebarContent
      )}

      {/* Main area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {/* Topbar */}
        <header
          style={{
            height: 56,
            padding: '0 24px',
            borderBottom: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            background: 'linear-gradient(180deg, var(--bg-1), color-mix(in oklch, var(--bg-0) 70%, transparent))',
            backdropFilter: 'blur(10px)',
            position: 'sticky',
            top: 0,
            zIndex: 4,
            flexShrink: 0,
          }}
        >
          {isMobile && (
            <IconButton
              aria-label="open sidebar"
              onClick={handleDrawerToggle}
              sx={{ color: 'var(--fg-2)', mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
            <span style={{ fontSize: 14, fontWeight: 500 }}>Admin</span>
            <span style={{ fontSize: 11.5, color: 'var(--fg-3)' }}>Platform management</span>
          </div>

          <span style={{ flex: 1 }} />

          {/* Ask anything button */}
          <button
            type="button"
            style={{
              all: 'unset',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              fontSize: 12.5,
              color: 'var(--fg-3)',
              border: '1px solid var(--line)',
              borderRadius: 8,
              background: 'transparent',
              transition: 'border-color .15s ease, color .15s ease',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent-line)';
              (e.currentTarget as HTMLElement).style.color = 'var(--fg-1)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = 'var(--line)';
              (e.currentTarget as HTMLElement).style.color = 'var(--fg-3)';
            }}
          >
            Ask anything <span className="kbd" style={{ marginLeft: 4 }}>⌘K</span>
          </button>

          {/* User avatar */}
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 99,
              background: 'linear-gradient(135deg, oklch(62% 0.12 22), oklch(48% 0.12 10))',
              fontSize: 12,
              fontWeight: 600,
              color: 'oklch(98% 0.02 22)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {userInitial}
          </div>
        </header>

        {/* Content */}
        <main style={{ flex: 1, overflowY: 'auto', padding: 'var(--pad)' }}>
          <AccessDeniedAlert />
          <Outlet />
        </main>
      </div>

      {/* Theme/density tweaks panel */}
      <TweaksPanel />
    </div>
  );
}

// ─── NavGroupSection ─────────────────────────────────────────────────────────

function NavGroupSection({
  title,
  items,
  currentPath,
  onNavigate,
}: {
  title: string;
  items: NavItem[];
  currentPath: string;
  onNavigate?: () => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 14 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          color: 'var(--fg-4)',
          padding: '4px 10px 8px',
        }}
      >
        {title}
      </div>
      {items.map((item) => (
        <SidebarNavItem
          key={item.path}
          item={item}
          active={item.path === '/admin' ? currentPath === '/admin' : currentPath.startsWith(item.path)}
          onNavigate={onNavigate}
        />
      ))}
    </div>
  );
}

// ─── SidebarNavItem ──────────────────────────────────────────────────────────

function SidebarNavItem({
  item,
  active,
  onNavigate,
}: {
  item: NavItem;
  active: boolean;
  onNavigate?: () => void;
}) {
  return (
    <Link
      to={item.path}
      onClick={onNavigate}
      style={{
        all: 'unset',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 10px',
        borderRadius: 8,
        color: active ? 'var(--fg-0)' : 'var(--fg-2)',
        background: active ? 'var(--bg-2)' : 'transparent',
        boxShadow: active ? 'inset 0 0 0 1px var(--line)' : 'none',
        fontSize: 13,
        fontWeight: active ? 500 : 400,
        position: 'relative',
        transition: 'background .15s ease, color .15s ease',
      }}
      onMouseEnter={(e) => {
        if (!active) (e.currentTarget as HTMLElement).style.background = 'var(--bg-1)';
      }}
      onMouseLeave={(e) => {
        if (!active) (e.currentTarget as HTMLElement).style.background = 'transparent';
      }}
    >
      <span style={{ color: active ? 'var(--accent)' : 'var(--fg-3)', display: 'inline-flex' }}>
        {item.icon}
      </span>
      <span style={{ flex: 1 }}>{item.label}</span>
    </Link>
  );
}
